<template>
  <div class="toast-host" role="region" aria-live="polite" aria-atomic="false">
    <transition-group name="toast" tag="div" class="toast-stack">
      <div
        v-for="t in toasts.toasts"
        :key="t.id"
        :class="['toast', `toast--${t.kind}`]"
        role="status"
      >
        <div class="toast-icon" :data-kind="t.kind">
          <Icon :name="iconFor(t.kind)" :size="14" />
        </div>
        <div class="toast-body">
          <div class="toast-title">{{ t.title }}</div>
          <div v-if="t.message" class="toast-msg">{{ t.message }}</div>
        </div>
        <button
          class="toast-close"
          type="button"
          aria-label="Fermer la notification"
          @click="toasts.dismiss(t.id)"
        >
          <Icon name="x" :size="12" />
        </button>
      </div>
    </transition-group>
  </div>
</template>

<script setup lang="ts">
import Icon from './Icon.vue'
import { useToasts, type ToastKind } from '@/shared/stores/toasts'

const toasts = useToasts()

function iconFor(kind: ToastKind): string {
  switch (kind) {
    case 'success': return 'check-circle'
    case 'error': return 'alert-circle'
    case 'warning': return 'alert-triangle'
    default: return 'info'
  }
}
</script>

<style scoped>
.toast-host {
  position: fixed;
  top: 16px;
  right: 16px;
  z-index: 9999;
  pointer-events: none;
}

.toast-stack {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 340px;
  max-width: calc(100vw - 32px);
}

.toast {
  pointer-events: auto;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 11px 12px 11px 12px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-left: 3px solid var(--primary);
  border-radius: var(--radius);
  box-shadow: var(--shadow-lg);
  font-size: 13px;
  color: var(--text);
}
.toast--success { border-left-color: var(--success); }
.toast--error { border-left-color: var(--danger); }
.toast--warning { border-left-color: var(--warning); }
.toast--info { border-left-color: var(--info); }

.toast-icon {
  width: 22px; height: 22px;
  border-radius: var(--radius-sm);
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
  color: var(--text-secondary);
}
.toast-icon[data-kind="success"] { background: var(--success-bg); color: var(--success); }
.toast-icon[data-kind="error"] { background: var(--danger-bg); color: var(--danger); }
.toast-icon[data-kind="warning"] { background: var(--warning-bg); color: var(--warning); }
.toast-icon[data-kind="info"] { background: var(--info-bg); color: var(--info); }

.toast-body { flex: 1; min-width: 0; }
.toast-title { font-weight: 600; line-height: 1.3; }
.toast-msg {
  margin-top: 2px;
  font-size: 12.5px;
  color: var(--text-muted);
  line-height: 1.35;
  word-wrap: break-word;
}

.toast-close {
  background: none;
  border: none;
  color: var(--text-disabled);
  cursor: pointer;
  padding: 2px;
  border-radius: var(--radius-xs);
  display: flex;
  align-items: center;
}
.toast-close:hover { background: var(--surface-hover); color: var(--text-secondary); }
.toast-close:focus-visible { outline: 2px solid var(--primary); outline-offset: 1px; }

/* ── Animations ─────────────────────────────────────────── */
.toast-enter-from {
  opacity: 0;
  transform: translateX(20px);
}
.toast-leave-to {
  opacity: 0;
  transform: translateX(20px);
}
.toast-enter-active,
.toast-leave-active {
  transition: opacity var(--duration-base) var(--ease-out),
              transform var(--duration-base) var(--ease-out);
}
.toast-move {
  transition: transform var(--duration-base) var(--ease-out);
}

@media (prefers-reduced-motion: reduce) {
  .toast-enter-active, .toast-leave-active, .toast-move { transition: none; }
}
</style>
